import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }
    // Bei mehreren Teams: Auswahl-Seite; sonst Dashboard
    if (!res.user.active_team_id) navigate("/mannschaft-waehlen");
    else navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      <div
        className="hidden lg:flex flex-1 relative items-end p-12"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, rgba(9,9,11,0.55), rgba(9,9,11,0.95)), url('https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxzb2NjZXIlMjBmaWVsZCUyMHR1cmZ8ZW58MHx8fHwxNzgxMjU1MTg0fDA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-lighter/40 mb-6">
            <span className="h-1.5 w-1.5 bg-brand-lighter" />
            <span className="text-[10px] uppercase tracking-wider text-brand-lighter">Coach Tool</span>
          </div>
          <h1 className="font-display text-5xl xl:text-6xl font-bold uppercase leading-[0.95] mb-4">
            Spieler<br />objektiv<br />einschätzen.
          </h1>
          <p className="text-zinc-400 max-w-sm leading-relaxed">
            Standardisierte Leistungsprofile für deine Mannschaft. Übersichtlich, schnell, fair.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-[480px] flex items-center justify-center p-6 sm:p-12 border-l border-zinc-900">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 bg-brand flex items-center justify-center rounded-md">
              <Shield className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-xl font-bold uppercase tracking-tight">Anmelden</div>
              <div className="text-xs uppercase tracking-wider text-zinc-500">Trainer-Bereich</div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-[10px] uppercase tracking-wide text-zinc-400">E-Mail</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 focus-visible:ring-brand-lighter focus-visible:border-brand-lighter h-11"
              placeholder="trainer@verein.de"
              data-testid="login-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-[10px] uppercase tracking-wide text-zinc-400">Passwort</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 focus-visible:ring-brand-lighter focus-visible:border-brand-lighter h-11"
              placeholder="••••••••"
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 p-3" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold h-11 rounded-sm"
            data-testid="login-submit-btn"
          >
            {loading ? "Anmelden…" : "Anmelden"}
          </Button>

          <p className="text-xs text-zinc-500 text-center">
            Noch kein Konto?{" "}
            <Link to="/register" className="text-brand-lighter hover:underline" data-testid="login-register-link">
              Jetzt registrieren
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
